import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthorizationRequest, DocumentsTabInfo } from '@core/model';
import { AuthenticationService } from '@core/service';
import { UploadFile } from '@shared/model';
import moment from 'moment/moment';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-auth-request-documents',
    templateUrl: './auth-request-documents.component.html',
    styleUrls: ['./auth-request-documents.component.scss'],
    standalone: false
})
export class AuthRequestDocumentsComponent implements OnInit {
  @Output() isStepFormValid = new EventEmitter<boolean>();
  @Output() authRequestChanged = new EventEmitter<AuthorizationRequest>();
  @Output() documentsChanged = new EventEmitter<UploadFile[]>();
  @Output() documentTabInfoChanged = new EventEmitter<DocumentsTabInfo>();
  @Output() uploadingOasisChange = new EventEmitter<boolean>();
  @Input() dateTimeFaxReceivedUTC: string = null;
  @Input() requestType: string;
  @Input() authRequest: AuthorizationRequest;
  @Input() uploadedFiles: UploadFile[] = [];
  @Input() documentsTabInfo: DocumentsTabInfo;
  @Input() tenantId: string;
  @Input() tenantName: string;
  @Input() isPASCompleteFlow = false;

  subsequentRequestType = 'Subsequent';
  public documentsForm: FormGroup;
  orderDateTypeLabel = 'Signed';
  isExternal = false;
  createdOn: string;
  header = '';

  constructor(
    private authenticationService: AuthenticationService,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.isExternal = this.authenticationService.isExternalUser;
    this.header = this.isExternal
      ? 'Process Is Almost Complete'
      : 'Process is almost complete. The following documents create this authorization request. Please select the document you are uploading now.';

    this.createdOn = this.isExternal
      ? moment().utc().toISOString()
      : this.authRequest.authorizationDetailDTOs[this.authRequest.authorizationDetailDTOs.length - 1].dateTimeFaxRecieved;
    this.initForm();
    this.isFormValid();

    this.setOrderDateTypeLabel(this.documentsForm.get('dateType').value);
  }

  initForm() {

    // Previously uploaded files not needed.
    if (!this.isExternal) {
      this.authRequest.uploadFileDTOs = [];
    }

    if (this.documentsTabInfo) {
      const { noOrder, dateType, signedVerbalOrderDate, historyAndPhysical, uploadOasis, verbalOrderingPhysician } = this.documentsTabInfo;

      this.documentsForm = new FormGroup({
        noOrder: new FormControl(noOrder),
        dateType: new FormControl(!dateType && this.isPASCompleteFlow ? 'verbalOrder' : dateType),
        signedVerbalOrderDate: new FormControl(
          !signedVerbalOrderDate && this.isPASCompleteFlow ? new Date() : signedVerbalOrderDate
        ),
        verbalOrderingPhysician: new FormControl(verbalOrderingPhysician),
        historyAndPhysical: new FormControl(historyAndPhysical),
        uploadOasis: new FormControl(uploadOasis, this.requestType !== 'Initial' ? [Validators.required] : [])
      });
      this.documentTabInfoChanged.emit(this.documentsForm.value);
      this.checkFormValidity();
    } else {
      this.documentsForm = new FormGroup({
        noOrder: new FormControl(false),
        dateType: new FormControl(''),
        signedVerbalOrderDate: new FormControl(''),
        verbalOrderingPhysician: new FormControl(''),
        historyAndPhysical: new FormControl(false),
        uploadOasis: new FormControl('', this.requestType !== 'Initial' ? [Validators.required] : [])
      });
    }

    // listen for type changes on date type
    this.documentsForm.get('dateType').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => this.setOrderDateTypeLabel(val));

    this.documentsForm.get('uploadOasis').valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(val => {
      if (val === 'Yes') {
        this.uploadingOasisChange.emit(true);
      } else if (val === 'No') {
        this.uploadingOasisChange.emit(false);
      }
    });

    if (this.uploadedFiles.length > 0)
      this.loadFiles(this.uploadedFiles);
  }

  // listen for when form is valid
  isFormValid() {
    this.documentsForm.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(val => {
      this.documentTabInfoChanged.emit(val);
      this.checkFormValidity();
      this.finalizeDataForAPI();
    });
  }

  checkFormValidity() {
     if (
       this.calculateFormValidity() === false &&
       this.requestType !== 'Initial'
     ) {
       this.isStepFormValid.emit(false);
     } else if (
       this.calculateFormValidity() === false &&
       this.requestType == 'Initial' && !this.isPASCompleteFlow
     ) {
       this.isStepFormValid.emit(false);
     } else if (
       this.isPASCompleteFlow &&
       this.requestType === 'Initial' &&
       this.calculateFormValidity() === false
     ) {
       this.isStepFormValid.emit(false);
     } else {
       this.isStepFormValid.emit(this.documentsForm.valid);
     }
  }

  calculateFormValidity(): boolean {
    let isValid = true;

    if (this.isPASCompleteFlow) {
      if (this.documentsForm.get('dateType').value === 'verbalOrder')
        isValid =
          this.documentsForm.get('signedVerbalOrderDate').value !== '' &&
          this.documentsForm.get('verbalOrderingPhysician').value !== '' &&
          this.documentsForm.get('verbalOrderingPhysician').value !== null;
      else {
        isValid =
          this.documentsForm.get('dateType').value === 'signedOrder' &&
          this.documentsForm.get('signedVerbalOrderDate').value !== '';
      }
    } else {
      if (this.requestType == 'Initial') {
        if (this.documentsForm.get('uploadOasis').value === '') {
          return false;
        }

        const hasOrderTypeWithDocument =
          this.documentsForm.get('dateType').value !== '' &&
          this.documentsForm.get('signedVerbalOrderDate').value !== '' &&
          this.documentsForm.get('signedVerbalOrderDate').value !== null &&
          this.uploadedFiles.length > 0;

        const hasClinicalDocWithDocument =
          this.documentsForm.get('historyAndPhysical').value === true &&
          this.uploadedFiles.length > 0;

        const hasNoDocuments =
          this.documentsForm.get('noOrder').value === true;

        if (hasOrderTypeWithDocument || hasClinicalDocWithDocument || hasNoDocuments) {
          isValid = true;
        } else {
          isValid = false;
        }
      } else {
        if (
          this.documentsForm.get('noOrder').value === false &&
          this.uploadedFiles.length === 0
        ) {
          return false;
        }
        if (this.documentsForm.get('uploadOasis').value === '') {
          return false;
        }

        if (
          (((this.documentsForm.get('dateType').value !== '' &&
            this.documentsForm.get('signedVerbalOrderDate').value !== '' &&
            this.documentsForm.get('signedVerbalOrderDate').value !== null) ||
            this.documentsForm.get('historyAndPhysical').value === true) &&
            this.uploadedFiles.length > 0) ||
          this.documentsForm.get('noOrder').value === true
        ) {
          isValid = true;
        } else {
          isValid = false;
        }
      }
    }
    return isValid;
  }

  // clear out previous data if no order selected
  deselectType(event) {
    if (event.checked === true) {
      this.documentsForm.controls['dateType'].setValue('');
      this.documentsForm.controls['signedVerbalOrderDate'].setValue('');
      this.documentsForm.controls['verbalOrderingPhysician'].setValue('');
      this.documentsForm.controls['historyAndPhysical'].setValue(false);
      this.uploadedFiles=[];
      this.loadFiles(this.uploadedFiles);
      this.documentsForm.updateValueAndValidity();
    }
  }

  // clear out no order if order type selected
  deselectdateType(event) {
    if (event.checked !== '') {
      this.documentsForm.controls['noOrder'].setValue(false);
      this.documentsForm.controls['historyAndPhysical'].setValue(false);
      this.documentsForm.controls['verbalOrderingPhysician'].setValue('');
      this.documentsForm.updateValueAndValidity();
    }
  }

  // clear all other type when user select the clinical type
  deselectOtherType(event) {
    if (event.checked === true) {
      this.documentsForm.controls['dateType'].setValue('');
      this.documentsForm.controls['signedVerbalOrderDate'].setValue('');
      this.documentsForm.controls['verbalOrderingPhysician'].setValue('');
      this.documentsForm.controls['noOrder'].setValue(false);
      this.documentTabInfoChanged.emit({
        noOrder: false,
        dateType: '',
        signedVerbalOrderDate: null,
        verbalOrderingPhysician: null,
        historyAndPhysical: true,
        uploadOasis: this.documentsForm.value.uploadOasis
      });
      this.documentsForm.updateValueAndValidity();
    }
  }

  // Move data into model
  finalizeDataForAPI() {
    const dateType = this.documentsForm.get('dateType').value;
    const signedVerbalOrderDate = this.documentsForm.get('signedVerbalOrderDate').value;
    const verbalOrderingPhysician = this.documentsForm.get('verbalOrderingPhysician').value;
    this.authRequest.authorizationDTO.hasMissingDocument = this.documentsForm.get('noOrder').value;
    if (dateType === 'signedOrder') {
      this.authRequest.authorizationDTO.signedOrderDate = signedVerbalOrderDate;
      this.authRequest.authorizationDTO.verbalOrderDate = null;
      this.authRequest.authorizationDTO.verbalOrderingPhysician = null;
    } else if (dateType === 'verbalOrder') {
      this.authRequest.authorizationDTO.verbalOrderDate = signedVerbalOrderDate;
      this.authRequest.authorizationDTO.signedOrderDate = null;
      this.authRequest.authorizationDTO.verbalOrderingPhysician = verbalOrderingPhysician;
    } else if (dateType == null) {
      this.authRequest.authorizationDTO.verbalOrderDate = null;
      this.authRequest.authorizationDTO.signedOrderDate = null;
      this.authRequest.authorizationDTO.verbalOrderingPhysician = null;
    }
  }

  loadFiles(uploadedFiles: UploadFile[]) {
    this.uploadedFiles = uploadedFiles;
    this.authRequest.uploadFileDTOs = uploadedFiles.reduce((newFiles, file) => {
      if (file.documentSource !== 'Inbound Faxes') {
        const {
          fileMetaData,
          ...newFile
        } = file;

        newFiles.push({...newFile, createdBy: this.authenticationService.getEmail});
      }

      return newFiles;
  }, []);
    this.checkFormValidity();
    this.documentsChanged.emit(this.uploadedFiles);
  }

  private setOrderDateTypeLabel(dateType: string): void {
    if (dateType === 'signedOrder') {
      this.orderDateTypeLabel = 'Signed';
    } else {
      this.orderDateTypeLabel = 'Verbal';
    }
  }
}
